import { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import Input from "@/components/ui/input";
import { useToast } from "@/hooks/useToast";
import Modal from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AppTheme } from "@/utils/constants/theme";
import { changePasswordMutation } from "@/api-services/auth.services";

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordValidation {
  length: boolean;
  uppercase: boolean;
  special: boolean;
  alphanumeric: boolean;
}

interface ChangePasswordModalProps {
  onCancel?: () => void;
  onSuccess?: () => void;
}

export interface ChangePasswordModalRef {
  open: () => void;
}

const ChangePasswordModal = forwardRef<ChangePasswordModalRef, ChangePasswordModalProps>(
  ({ onCancel, onSuccess }, ref) => {
    const { onShowToast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [logoutAfterChange, setLogoutAfterChange] = useState(false); // Default: stay logged in (unchecked)
    const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
      length: false,
      uppercase: false,
      special: false,
      alphanumeric: false,
    });

    const {
      register,
      handleSubmit,
      reset,
      watch,
      trigger,
      setError,
      formState: { errors, isValid },
    } = useForm<ChangePasswordFormData>({
      mode: "onChange", // Enable real-time validation
      defaultValues: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
    });

    const newPassword = watch("newPassword", "");
    const currentPassword = watch("currentPassword", "");
    const confirmPassword = watch("confirmPassword", "");

    useEffect(() => {
      if (newPassword) {
        setPasswordValidation({
          length: newPassword.length >= 8,
          uppercase: /[A-Z]/.test(newPassword),
          special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
          alphanumeric: /(?=.*[a-zA-Z])(?=.*[0-9])/.test(newPassword),
        });
      } else {
        setPasswordValidation({
          length: false,
          uppercase: false,
          special: false,
          alphanumeric: false,
        });
      }
    }, [newPassword]);

    // Re-validate confirm password when new password changes
    useEffect(() => {
      if (confirmPassword) {
        trigger("confirmPassword");
      }
    }, [newPassword, confirmPassword, trigger]);

    // Re-validate new password when current password changes
    // This ensures "New password must be different from current password" check updates
    useEffect(() => {
      if (newPassword) {
        trigger("newPassword");
      }
    }, [currentPassword, newPassword, trigger]);

    const validatePassword = (value: string): boolean | string => {
      if (value.length < 8) return "Password must be at least 8 characters";
      if (!/[A-Z]/.test(value))
        return "Password must contain at least 1 uppercase letter";
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(value))
        return "Password must contain at least 1 special character";
      if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(value))
        return "Password must contain both letters and numbers";
      // Check if new password is different from current password
      if (value === currentPassword) {
        return "New password must be different from current password";
      }
      return true;
    };

    const onSubmit = async (data: ChangePasswordFormData) => {
      try {
        setIsSubmitting(true);

        await changePasswordMutation({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
          logoutOtherDevices: logoutAfterChange, // Send flag to backend to invalidate other sessions
        });

        onShowToast({
          type: "success",
          title: "Password Changed!",
          description: logoutAfterChange 
            ? "Your password has been changed and all other devices have been logged out."
            : "Your password has been changed successfully.",
        });

        reset();
        onSuccess?.();
        setIsOpen(false);

        // If user chose to log out current device too (not just other devices)
        // Note: logoutOtherDevices only logs out OTHER devices, not current one
        // If you want to also log out current device when checked, uncomment below:
        // if (logoutAfterChange) {
        //   try {
        //     await LogoutMutation();
        //     onClearStorage();
        //     nav('/auth/login');
        //   } catch (error) {
        //     console.error('Logout error:', error);
        //     onClearStorage();
        //     nav('/auth/login');
        //   }
        // }
      } catch (error: any) {
        // Backend returns error in 'error' field, not 'message'
        const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || "Something went wrong. Please try again later.";
        const status = error?.response?.status;
        
        // Check if error is due to incorrect current password
        // Backend returns: { success: false, error: "Current password is incorrect" } with status 401
        const isCurrentPasswordError = status === 401 && 
            (errorMessage.toLowerCase().includes("current password") || 
             errorMessage.toLowerCase().includes("incorrect") ||
             errorMessage === "Current password is incorrect");
        
        if (isCurrentPasswordError) {
          // Set inline error on current password field
          setError("currentPassword", {
            type: "manual",
            message: "Current password is incorrect. Please try again.",
          }, { shouldFocus: false });
        } else {
          // For other errors, show toast only
          onShowToast({
            type: "error",
            title: "Failed to Change Password",
            description: errorMessage,
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true);
        // Auto-focus first field after modal opens
        setTimeout(() => {
          const firstInput = document.querySelector('input[placeholder="Current Password"]') as HTMLInputElement;
          firstInput?.focus();
        }, 100);
      },
    }));

    const handleClose = () => {
      setIsOpen(false);
      reset();
      setLogoutAfterChange(false); // Reset to default (unchecked)
      onCancel?.();
    };

    return (
      <Modal
        title={"Change Password"}
        footer={
          <div className="flex gap-2 w-full p-2">
            <Button
              variant="primary-outline"
              onClick={handleClose}
              className="grow"
              size="lg"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
              className="grow"
              size="lg"
              disabled={isSubmitting || !isValid || Object.keys(errors).length > 0}
            >
              {isSubmitting ? "Changing..." : "Change Password"}
            </Button>
          </div>
        }
        open={isOpen}
        headerBackgroundColor={AppTheme.softPeach}
        onCancel={handleClose}
        widthPercent={45}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Input
              type="password"
              placeholder="Current Password"
              {...register("currentPassword", {
                required: "Current password is required",
              })}
              err={errors.currentPassword?.message}
            />
          </div>

          <div>
            <Input
              type="password"
              placeholder="New Password"
              {...register("newPassword", {
                required: "New password is required",
                validate: validatePassword,
              })}
              err={errors.newPassword?.message}
            />
          </div>

          <div>
            <Input
              type="password"
              placeholder="Confirm New Password"
              {...register("confirmPassword", {
                required: "Please confirm your new password",
                validate: (value) => {
                  if (!value) {
                    return "Please confirm your new password";
                  }
                  if (value !== newPassword) {
                    return "Passwords do not match";
                  }
                  return true;
                },
              })}
              err={errors.confirmPassword?.message}
              onBlur={() => trigger("confirmPassword")}
            />
          </div>

          {newPassword && (
            <div className="text-sm text-gray-500 space-y-1 font-poppins">
              <p className="font-poppins font-medium">Password requirements:</p>
              <ul className="list-disc pl-5 text-xs space-y-1 font-poppins">
                <li
                  className={`font-poppins ${
                    passwordValidation.length ? "text-green-500" : ""
                  }`}
                >
                  At least 8 characters in length
                </li>
                <li
                  className={`font-poppins ${
                    passwordValidation.uppercase ? "text-green-500" : ""
                  }`}
                >
                  1 uppercase letter
                </li>
                <li
                  className={`font-poppins ${
                    passwordValidation.special ? "text-green-500" : ""
                  }`}
                >
                  1 special character
                </li>
                <li
                  className={`font-poppins ${
                    passwordValidation.alphanumeric ? "text-green-500" : ""
                  }`}
                >
                  A combination of letters & numbers
                </li>
              </ul>
            </div>
          )}

          <div className="pt-2">
            <Checkbox
              id="logoutAfterChange"
              checked={logoutAfterChange}
              onCheckedChange={(checked) => setLogoutAfterChange(checked === true)}
              disabled={isSubmitting}
              label="Log out of other devices. Choose this if someone else used your account."
            />
          </div>
        </form>
      </Modal>
    );
  }
);

ChangePasswordModal.displayName = "ChangePasswordModal";

export default ChangePasswordModal;

